import React, { useEffect, useMemo, useRef, useState } from "react";
import { animate } from "animejs";
import { createDraggable } from "animejs/draggable";
import TicketCard from "./TicketCard";

const FREEFORM_TICKET_BASE_LAYER = 1;
const FREEFORM_TICKET_ACTIVE_LAYER = 20;
const FREEFORM_TICKET_BASE_WIDTH = 420;
const FREEFORM_TICKET_MIN_SCALE = 0.6;
const FREEFORM_TICKET_MAX_SCALE = 1.8;

function clampScale(value) {
  return Math.max(FREEFORM_TICKET_MIN_SCALE, Math.min(FREEFORM_TICKET_MAX_SCALE, value));
}

function getWidthForScale(scale) {
  return `${Math.round(FREEFORM_TICKET_BASE_WIDTH * clampScale(scale))}px`;
}

function getScaleForLayout(layout) {
  return clampScale(Number(layout?.scale) || 1);
}

function getDistanceBetweenTouches(touches) {
  if (!touches || touches.length < 2) return 0;
  const [first, second] = touches;
  return Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
}

function getTrackedTouches(touchList, trackedIds) {
  if (!touchList || !trackedIds || trackedIds.length < 2) return [];
  const next = [];
  for (const trackedId of trackedIds) {
    const match = Array.from(touchList).find((touch) => touch.identifier === trackedId);
    if (!match) return [];
    next.push(match);
  }
  return next;
}

function getTouchMidpoint(touches) {
  if (!touches || touches.length < 2) return null;
  const [first, second] = touches;
  return {
    x: (first.clientX + second.clientX) / 2,
    y: (first.clientY + second.clientY) / 2,
  };
}

function clampPositionToBoard(x, y, boardRect, nodeRect) {
  if (!boardRect || !nodeRect) {
    return { x, y };
  }
  const maxX = Math.max(0, boardRect.width - nodeRect.width);
  const maxY = Math.max(0, boardRect.height - nodeRect.height);
  return {
    x: Math.max(0, Math.min(x, maxX)),
    y: Math.max(0, Math.min(y, maxY)),
  };
}

function toKey(ticketInstanceId, card = 0) {
  return `${ticketInstanceId}::${Number.isFinite(Number(card)) ? Number(card) : 0}`;
}

function getCurrentCourseNumber(order) {
  const realItems = Array.isArray(order?.items)
    ? order.items.filter(
        (item) =>
          item &&
          !item.isCourseSeparator &&
          !item.isMessage &&
          !item.isFireItem &&
          item.course !== undefined &&
          item.course !== null
      )
    : [];

  if (!realItems.length) return Number.POSITIVE_INFINITY;

  const unbumpedCourses = realItems
    .filter((item) => !item.isBumped)
    .map((item) => Number(item.course))
    .filter((course) => Number.isFinite(course))
    .sort((a, b) => a - b);

  if (unbumpedCourses.length) return unbumpedCourses[0] + 1;

  const allCourses = realItems
    .map((item) => Number(item.course))
    .filter((course) => Number.isFinite(course))
    .sort((a, b) => a - b);

  return allCourses.length ? allCourses[allCourses.length - 1] + 1 : Number.POSITIVE_INFINITY;
}

function getOrderCreatedMs(order) {
  const itemStartMs = Array.isArray(order?.items)
    ? order.items
        .map((item) => new Date(item?.createdAt).getTime())
        .filter((ms) => Number.isFinite(ms))
    : [];

  const orderCreatedMs = new Date(order?.createdAt).getTime();
  if (itemStartMs.length > 0) {
    return Math.min(...itemStartMs);
  }
  if (Number.isFinite(orderCreatedMs)) {
    return orderCreatedMs;
  }
  return Number.MAX_SAFE_INTEGER;
}

function getOrderItemCount(order) {
  return Array.isArray(order?.items)
    ? order.items.filter(
        (item) =>
          item &&
          !item.isParked &&
          String(item.status || "").toLowerCase() !== "parked" &&
          !item.isCourseSeparator &&
          !item.isMessage &&
          !item.isFireItem
      ).length
    : 0;
}

function getOrderHasFiredItems(order) {
  return Array.isArray(order?.items)
    ? order.items.some(
        (item) =>
          item &&
          !item.isCourseSeparator &&
          !item.isMessage &&
          !item.isFireItem &&
          !item.isBumped &&
          !!item.isFired
      )
    : false;
}

export default function FreeformBoard({
  stationId,
  cards,
  layouts,
  sortRequest = null,
  onBringToFront,
  onPersistLayout,
  onDragStateChange,
  onItemTap,
  onItemDoubleTap,
  onPrepItemReady,
  onBump,
  settings,
  isPeekMode,
  isFireCourseMode,
  onSelectFireCourse,
  setIsFireCourseMode,
  isFireItemMode,
  onSelectFireItem,
  isPrintCourseMode,
  onSelectPrintCourse,
  isPrintTicketMode,
  setPeekedOrder,
  setPeekModeIntent,
  onBumpCourse,
  onCourseComplete,
  onPeekSelect,
  isLockXAxisActive = false,
  isLockYAxisActive = false,
}) {
  const elementRefs = useRef(new Map());
  const contentRefs = useRef(new Map());
  const draggableRefs = useRef(new Map());
  const gestureCleanupRefs = useRef(new Map());
  const resizeObserverRefs = useRef(new Map());
  const updateScaledFrameRefs = useRef(new Map());
  const animationRefs = useRef(new Map());
  const dragStartRefs = useRef(new Map());
  const boardFrameRef = useRef(null);
  const boardSurfaceRef = useRef(null);
  const lastSortRequestRef = useRef(null);
  const [boardHeight, setBoardHeight] = useState(null);
  const lockXAxisRef = useRef(isLockXAxisActive);
  const lockYAxisRef = useRef(isLockYAxisActive);

  useEffect(() => {
    lockXAxisRef.current = isLockXAxisActive;
  }, [isLockXAxisActive]);

  useEffect(() => {
    lockYAxisRef.current = isLockYAxisActive;
  }, [isLockYAxisActive]);

  const applyAxisLocks = (key, x, y) => {
    const start = dragStartRefs.current.get(key);
    return {
      x: lockXAxisRef.current && start ? start.x : x,
      y: lockYAxisRef.current && start ? start.y : y,
    };
  };

  const orderedCards = useMemo(() => {
    return [...cards].sort((left, right) => {
      const leftLayout = layouts[toKey(left.order.ticket_instance_id, left.order.card)] || {};
      const rightLayout = layouts[toKey(right.order.ticket_instance_id, right.order.card)] || {};
      return (Number(leftLayout.zIndex) || 0) - (Number(rightLayout.zIndex) || 0);
    });
  }, [cards, layouts]);

  const cssLayerByKey = useMemo(() => {
    const next = new Map();
    orderedCards.forEach(({ order }, index) => {
      next.set(
        toKey(order.ticket_instance_id, order.card),
        FREEFORM_TICKET_BASE_LAYER + index
      );
    });
    return next;
  }, [orderedCards]);

  const sortOrderedCards = useMemo(() => {
    return [...cards].sort((left, right) => {
      const createdDiff = getOrderCreatedMs(left.order) - getOrderCreatedMs(right.order);
      if (createdDiff !== 0) return createdDiff;
      return String(left.id || "").localeCompare(String(right.id || ""));
    });
  }, [cards]);

  useEffect(() => {
    const elementMap = elementRefs.current;
    const draggableMap = draggableRefs.current;
    const nextKeys = new Set();

    orderedCards.forEach(({ order }) => {
      const key = toKey(order.ticket_instance_id, order.card);
      nextKeys.add(key);
      const node = elementMap.get(key);
      const contentNode = contentRefs.current.get(key);
      if (!node || !contentNode) return;

      const layout = layouts[key] || { x: 24, y: 24, zIndex: 0 };
      let draggable = draggableMap.get(key);
      const shouldDisableX = !!isLockXAxisActive;
      const shouldDisableY = !!isLockYAxisActive;
      const disabledStateMatches =
        draggable &&
        Array.isArray(draggable.disabled) &&
        Boolean(draggable.disabled[0]) === shouldDisableX &&
        Boolean(draggable.disabled[1]) === shouldDisableY;

      if (draggable && !disabledStateMatches) {
        animationRefs.current.get(key)?.pause?.();
        animationRefs.current.delete(key);
        gestureCleanupRefs.current.get(key)?.();
        gestureCleanupRefs.current.delete(key);
        dragStartRefs.current.delete(key);
        draggable.disable();
        draggable.stop();
        draggableMap.delete(key);
        draggable = null;
      }

      const updateScaledFrame = (scaleValue, { resizeFrame = true } = {}) => {
        const safeScale = clampScale(scaleValue);
        node.dataset.rawScale = String(safeScale);
        contentNode.style.transform = `scale(${safeScale})`;
        if (!resizeFrame) return;
        const baseHeight = contentNode.offsetHeight || 0;
        node.style.width = getWidthForScale(safeScale);
        node.style.height = `${Math.round(baseHeight * safeScale)}px`;
      };
      updateScaledFrameRefs.current.set(key, updateScaledFrame);
      const persistLayout = (instance) => {
        onPersistLayout?.({
          key,
          stationId,
          ticketInstanceId: order.ticket_instance_id,
          orderId: order.order_id,
          card: Number(order.card) || 0,
          x: Math.round(instance.x),
          y: Math.round(instance.y),
          zIndex: Number(instance.$target.dataset.rawZIndex || layout.zIndex || 0),
          scale: clampScale(Number(instance.$target.dataset.rawScale) || getScaleForLayout(layout)),
        });
      };

      if (!draggable) {
        draggable = createDraggable(node, {
          trigger: node.querySelector(".freeform-drag-handle"),
          container: () => boardSurfaceRef.current,
          x: shouldDisableX ? false : true,
          y: shouldDisableY ? false : true,
          dragThreshold: { mouse: 4, touch: 14 },
          releaseContainerFriction: 1,
          containerFriction: 1,
          releaseMass: 1,
          releaseStiffness: 1000,
          releaseDamping: 500,
          releaseEase: "linear",
          onGrab: (instance) => {
            onDragStateChange?.(true);
            const nextZ = onBringToFront?.(key) ?? layout.zIndex ?? 0;
            instance.$target.dataset.rawZIndex = String(nextZ);
            instance.$target.style.zIndex = String(FREEFORM_TICKET_ACTIVE_LAYER);
            dragStartRefs.current.set(key, {
              x: Number(instance.x) || 0,
              y: Number(instance.y) || 0,
            });
          },
          onDrag: (instance) => {
            const nextX = Number(instance.x) || 0;
            const nextY = Number(instance.y) || 0;
            const locked = applyAxisLocks(key, nextX, nextY);
            if (locked.x !== nextX || locked.y !== nextY) {
              instance.setX(locked.x, true);
              instance.setY(locked.y, true);
            }
          },
          onRelease: (instance) => {
            instance.stop();
            onDragStateChange?.(false);
            const locked = applyAxisLocks(
              key,
              Number(instance.x) || 0,
              Number(instance.y) || 0
            );
            instance.setX(locked.x, true);
            instance.setY(locked.y, true);
            dragStartRefs.current.delete(key);
            persistLayout(instance);
          },
          onSettle: (instance) => {
            onDragStateChange?.(false);
            const locked = applyAxisLocks(
              key,
              Number(instance.x) || 0,
              Number(instance.y) || 0
            );
            instance.setX(locked.x, true);
            instance.setY(locked.y, true);
            dragStartRefs.current.delete(key);
            persistLayout(instance);
          },
        });
        draggableMap.set(key, draggable);
      }

      if (!gestureCleanupRefs.current.has(key)) {
        let pinchState = null;

        const handleTouchStart = (event) => {
          if (event.touches.length < 2) return;
          const trackedTouches = [event.touches[0], event.touches[1]];
          const midpoint = getTouchMidpoint(trackedTouches);
          const nodeRect = node.getBoundingClientRect();
          const startingScale = clampScale(
            Number(node.dataset.rawScale) || getScaleForLayout(layout)
          );
          if (!midpoint) return;
          const clampedStartScale = clampScale(startingScale);
          pinchState = {
            touchIds: trackedTouches.map((touch) => touch.identifier),
            startDistance: getDistanceBetweenTouches(trackedTouches),
            startScale: clampedStartScale,
            localX: (midpoint.x - nodeRect.left) / clampedStartScale,
            localY: (midpoint.y - nodeRect.top) / clampedStartScale,
          };
          if (!pinchState.startDistance) {
            pinchState = null;
            return;
          }
          updateScaledFrame(startingScale);
          draggable.stop();
          onDragStateChange?.(false);
          event.preventDefault();
        };

        const handleTouchMove = (event) => {
          if (!pinchState || event.touches.length < 2) return;
          const trackedTouches = getTrackedTouches(event.touches, pinchState.touchIds);
          if (trackedTouches.length < 2) return;
          const nextDistance = getDistanceBetweenTouches(trackedTouches);
          const midpoint = getTouchMidpoint(trackedTouches);
          const boardRect = boardSurfaceRef.current?.getBoundingClientRect?.();
          if (!nextDistance || !pinchState.startDistance) return;
          const nextScale = clampScale(
            pinchState.startScale * (nextDistance / pinchState.startDistance)
          );
          updateScaledFrame(nextScale);
          if (midpoint && boardRect) {
            const desiredX = midpoint.x - boardRect.left - pinchState.localX * nextScale;
            const desiredY = midpoint.y - boardRect.top - pinchState.localY * nextScale;
            const nextNodeRect = node.getBoundingClientRect();
            const clamped = clampPositionToBoard(desiredX, desiredY, boardRect, nextNodeRect);
            const locked = applyAxisLocks(key, clamped.x, clamped.y);
            draggable.setX(locked.x, true);
            draggable.setY(locked.y, true);
          }
          draggable.stop();
          onDragStateChange?.(false);
          event.preventDefault();
        };

        const finishPinch = () => {
          if (!pinchState) return;
          pinchState = null;
          persistLayout(draggable);
        };

        const handleTouchEnd = (event) => {
          if (pinchState) {
            const trackedTouches = getTrackedTouches(event.touches, pinchState.touchIds);
            if (trackedTouches.length >= 2) {
              pinchState = {
                ...pinchState,
                touchIds: trackedTouches.map((touch) => touch.identifier),
                startDistance: getDistanceBetweenTouches(trackedTouches),
                startScale: clampScale(Number(node.dataset.rawScale) || pinchState.startScale),
              };
              return;
            }
          }
          finishPinch();
        };

        const handleTouchCancel = () => {
          finishPinch();
        };

        node.addEventListener("touchstart", handleTouchStart, { passive: false });
        node.addEventListener("touchmove", handleTouchMove, { passive: false });
        node.addEventListener("touchend", handleTouchEnd);
        node.addEventListener("touchcancel", handleTouchCancel);

        gestureCleanupRefs.current.set(key, () => {
          node.removeEventListener("touchstart", handleTouchStart);
          node.removeEventListener("touchmove", handleTouchMove);
          node.removeEventListener("touchend", handleTouchEnd);
          node.removeEventListener("touchcancel", handleTouchCancel);
        });
      }

      if (!resizeObserverRefs.current.has(key) && typeof ResizeObserver !== "undefined") {
        const observer = new ResizeObserver(() => {
          updateScaledFrame(Number(node.dataset.rawScale) || getScaleForLayout(layout));
        });
        observer.observe(contentNode);
        resizeObserverRefs.current.set(key, observer);
      }

      node.dataset.rawZIndex = String(Number(layout.zIndex) || 0);
      node.style.zIndex = String(cssLayerByKey.get(key) || FREEFORM_TICKET_BASE_LAYER);
      updateScaledFrame(getScaleForLayout(layout));
      draggable.setX(Number(layout.x) || 0, true);
      draggable.setY(Number(layout.y) || 0, true);
    });

    for (const [key, draggable] of draggableMap.entries()) {
      if (nextKeys.has(key)) continue;
      animationRefs.current.get(key)?.pause?.();
      animationRefs.current.delete(key);
      gestureCleanupRefs.current.get(key)?.();
      gestureCleanupRefs.current.delete(key);
      resizeObserverRefs.current.get(key)?.disconnect?.();
      resizeObserverRefs.current.delete(key);
      updateScaledFrameRefs.current.delete(key);
      dragStartRefs.current.delete(key);
      draggable.disable();
      draggable.stop();
      draggableMap.delete(key);
      elementMap.delete(key);
      contentRefs.current.delete(key);
    }
  }, [cssLayerByKey, isLockXAxisActive, isLockYAxisActive, layouts, onBringToFront, onDragStateChange, onPersistLayout, orderedCards, stationId]);

  useEffect(() => {
    return () => {
      gestureCleanupRefs.current.forEach((cleanup) => cleanup());
      gestureCleanupRefs.current.clear();
      resizeObserverRefs.current.forEach((observer) => observer.disconnect?.());
      resizeObserverRefs.current.clear();
      updateScaledFrameRefs.current.clear();
      animationRefs.current.forEach((animation) => animation.pause?.());
      animationRefs.current.clear();
      dragStartRefs.current.clear();
      draggableRefs.current.forEach((draggable) => {
        draggable.disable();
        draggable.stop();
      });
      draggableRefs.current.clear();
      onDragStateChange?.(false);
    };
  }, [onDragStateChange]);

  useEffect(() => {
    const recomputeBoardHeight = () => {
      const parentHeight = boardFrameRef.current?.parentElement?.clientHeight || 0;
      let maxBottom = 0;

      orderedCards.forEach(({ order }) => {
        const key = toKey(order.ticket_instance_id, order.card);
        const node = elementRefs.current.get(key);
        const layout = layouts[key] || {};
        if (!node) return;
        const nodeHeight = node.offsetHeight || 0;
        const bottom = (Number(layout.y) || 0) + nodeHeight;
        if (bottom > maxBottom) {
          maxBottom = bottom;
        }
      });

      const nextHeight = Math.max(parentHeight, Math.ceil(maxBottom + 24));
      setBoardHeight(nextHeight || parentHeight || null);
    };

    recomputeBoardHeight();

    const frameNode = boardFrameRef.current;
    const observers = [];

    if (typeof ResizeObserver !== "undefined") {
      if (frameNode?.parentElement) {
        const parentObserver = new ResizeObserver(() => recomputeBoardHeight());
        parentObserver.observe(frameNode.parentElement);
        observers.push(parentObserver);
      }

      orderedCards.forEach(({ order }) => {
        const key = toKey(order.ticket_instance_id, order.card);
        const node = elementRefs.current.get(key);
        if (!node) return;
        const nodeObserver = new ResizeObserver(() => recomputeBoardHeight());
        nodeObserver.observe(node);
        observers.push(nodeObserver);
      });
    }

    return () => {
      observers.forEach((observer) => observer.disconnect?.());
    };
  }, [layouts, orderedCards]);

  useEffect(() => {
    if (!sortRequest?.id || sortRequest.id === lastSortRequestRef.current) return;
    lastSortRequestRef.current = sortRequest.id;

    const boardNode = boardSurfaceRef.current;
    if (!boardNode || sortOrderedCards.length === 0) return;

    const boardRect = boardNode.getBoundingClientRect();
    const horizontalPadding = 24;
    const verticalPadding = 24;
    const gap = 16;
    const targetLayouts = [];
    const mode = sortRequest.mode || "rows-by-oldest";
    const itemCountDescOrderedCards = [...cards].sort((left, right) => {
      const countDiff = getOrderItemCount(right.order) - getOrderItemCount(left.order);
      if (countDiff !== 0) return countDiff;
      const createdDiff = getOrderCreatedMs(left.order) - getOrderCreatedMs(right.order);
      if (createdDiff !== 0) return createdDiff;
      return String(left.id || "").localeCompare(String(right.id || ""));
    });
    const itemCountAscOrderedCards = [...itemCountDescOrderedCards].reverse();
    const ageAscOrderedCards = sortOrderedCards;
    const ageDescOrderedCards = [...sortOrderedCards].reverse();
    const firedFirstOrderedCards = [...cards].sort((left, right) => {
      const firedDiff =
        Number(getOrderHasFiredItems(right.order)) - Number(getOrderHasFiredItems(left.order));
      if (firedDiff !== 0) return firedDiff;
      const createdDiff = getOrderCreatedMs(left.order) - getOrderCreatedMs(right.order);
      if (createdDiff !== 0) return createdDiff;
      return String(left.id || "").localeCompare(String(right.id || ""));
    });
    const layoutCards =
      mode === "rows-by-oldest"
        ? ageAscOrderedCards
      : mode === "rows-by-newest"
        ? ageDescOrderedCards
      : mode === "rows-fired-first"
        ? firedFirstOrderedCards
      : mode === "column-by-oldest"
        ? ageAscOrderedCards
      : mode === "column-by-newest"
        ? ageDescOrderedCards
      : mode === "column-fired-first"
        ? firedFirstOrderedCards
        : mode === "stack-by-oldest"
          ? ageAscOrderedCards
        : mode === "stack-by-newest"
          ? ageDescOrderedCards
        : mode === "rows-by-most-items"
          ? itemCountDescOrderedCards
        : mode === "rows-by-least-items"
          ? itemCountAscOrderedCards
        : mode === "column-by-most-items"
          ? itemCountDescOrderedCards
        : mode === "column-by-least-items"
          ? itemCountAscOrderedCards
        : mode === "stack-by-most-items"
          ? itemCountDescOrderedCards
        : mode === "stack-by-least-items"
          ? itemCountAscOrderedCards
        : sortOrderedCards;

    if (
      mode === "stack-by-oldest" ||
      mode === "stack-by-newest" ||
      mode === "stack-by-most-items" ||
      mode === "stack-by-least-items"
    ) {
      const stackScale = FREEFORM_TICKET_MIN_SCALE;
      const stackOffsetX = 18;
      const stackOffsetY = 28;

      layoutCards.forEach(({ order }, index) => {
        const key = toKey(order.ticket_instance_id, order.card);
        targetLayouts.push({
          key,
          ticketInstanceId: order.ticket_instance_id,
          orderId: order.order_id,
          card: Number(order.card) || 0,
          scale: stackScale,
          x: Math.round(horizontalPadding + index * stackOffsetX),
          y: Math.round(verticalPadding + index * stackOffsetY),
          zIndex: index + 1,
        });
      });
    } else if (
      mode === "column-by-oldest" ||
      mode === "column-by-newest" ||
      mode === "column-by-most-items" ||
      mode === "column-by-least-items" ||
      mode === "column-fired-first"
    ) {
      const rowScale = FREEFORM_TICKET_MIN_SCALE;
      const rowOffsetY = 32;

      layoutCards.forEach(({ order }, index) => {
        const key = toKey(order.ticket_instance_id, order.card);
        targetLayouts.push({
          key,
          ticketInstanceId: order.ticket_instance_id,
          orderId: order.order_id,
          card: Number(order.card) || 0,
          scale: rowScale,
          x: Math.round(horizontalPadding),
          y: Math.round(verticalPadding + index * rowOffsetY),
          zIndex: index + 1,
        });
      });
    } else if (mode === "course-columns") {
      const groupedCards = layoutCards.reduce((groups, card) => {
        const courseNumber = getCurrentCourseNumber(card.order);
        const groupKey = Number.isFinite(courseNumber) ? String(courseNumber) : "done";
        if (!groups.has(groupKey)) {
          groups.set(groupKey, []);
        }
        groups.get(groupKey).push(card);
        return groups;
      }, new Map());

      const sortedGroupEntries = [...groupedCards.entries()].sort(([leftKey], [rightKey]) => {
        if (leftKey === "done") return 1;
        if (rightKey === "done") return -1;
        return Number(leftKey) - Number(rightKey);
      });

      const columnCount = Math.max(1, sortedGroupEntries.length);
      const stackOffsetY = 28;
      const maxCardsPerColumn = sortedGroupEntries.reduce(
        (largest, [, columnCards]) => Math.max(largest, columnCards.length),
        1
      );
      const minColumnFootprintWidth = FREEFORM_TICKET_BASE_WIDTH * FREEFORM_TICKET_MIN_SCALE;
      const availableWidth = Math.max(
        0,
        boardRect.width - horizontalPadding * 2 - gap * Math.max(0, columnCount - 1)
      );
      const widthScale = availableWidth / Math.max(1, columnCount * minColumnFootprintWidth);
      const targetScale = clampScale(Math.min(widthScale, 1));
      const scaledCardWidth = FREEFORM_TICKET_BASE_WIDTH * targetScale;
      const scaledStackOffsetY = stackOffsetY * targetScale;
      const columnFootprintWidth = scaledCardWidth;

      let nextZIndex = 1;

      sortedGroupEntries.forEach(([, columnCards], columnIndex) => {
        const columnX = Math.round(horizontalPadding + columnIndex * (columnFootprintWidth + gap));

        columnCards.forEach(({ order }, stackIndex) => {
          const key = toKey(order.ticket_instance_id, order.card);

          targetLayouts.push({
            key,
            ticketInstanceId: order.ticket_instance_id,
            orderId: order.order_id,
            card: Number(order.card) || 0,
            scale: targetScale,
            x: Math.round(columnX),
            y: Math.round(verticalPadding + stackIndex * scaledStackOffsetY),
            zIndex: nextZIndex,
          });

          nextZIndex += 1;
        });
      });
    } else if (mode === "rows-by-current-course") {
      const groupedCards = layoutCards.reduce((groups, card) => {
        const courseNumber = getCurrentCourseNumber(card.order);
        const groupKey = Number.isFinite(courseNumber) ? String(courseNumber) : "done";
        if (!groups.has(groupKey)) {
          groups.set(groupKey, []);
        }
        groups.get(groupKey).push(card);
        return groups;
      }, new Map());

      const sortedGroupEntries = [...groupedCards.entries()].sort(([leftKey], [rightKey]) => {
        if (leftKey === "done") return 1;
        if (rightKey === "done") return -1;
        return Number(leftKey) - Number(rightKey);
      });

      const minScaledWidth = FREEFORM_TICKET_BASE_WIDTH * FREEFORM_TICKET_MIN_SCALE;
      const maxColumnsThatFit = Math.max(
        1,
        Math.floor((Math.max(0, boardRect.width - horizontalPadding * 2) + gap) / (minScaledWidth + gap))
      );
      const wrappedGroupEntries = sortedGroupEntries.map(([groupKey, rowCards]) => {
        const subrows = [];
        for (let index = 0; index < rowCards.length; index += maxColumnsThatFit) {
          subrows.push(rowCards.slice(index, index + maxColumnsThatFit));
        }
        return [groupKey, subrows];
      });
      const widestRowCount = Math.max(
        1,
        ...wrappedGroupEntries.flatMap(([, subrows]) => subrows.map((subrow) => subrow.length))
      );
      const availableWidth = Math.max(
        0,
        boardRect.width - horizontalPadding * 2 - gap * Math.max(0, widestRowCount - 1)
      );
      const widthScale = availableWidth / Math.max(1, widestRowCount * FREEFORM_TICKET_BASE_WIDTH);
      const targetScale = clampScale(Math.min(widthScale, 1));
      const scaledCardWidth = FREEFORM_TICKET_BASE_WIDTH * targetScale;
      const rowHeights = wrappedGroupEntries.map(([, subrows]) => {
        return subrows.map((subrow) => {
          let maxHeight = 0;
          subrow.forEach(({ order }) => {
            const key = toKey(order.ticket_instance_id, order.card);
            const contentNode = contentRefs.current.get(key);
            const baseHeight = contentNode?.offsetHeight || 0;
            maxHeight = Math.max(maxHeight, baseHeight);
          });
          return maxHeight * targetScale;
        });
      });

      let nextZIndex = 1;
      let runningYOffset = 0;

      wrappedGroupEntries.forEach(([, subrows], groupIndex) => {
        subrows.forEach((subrow, subrowIndex) => {
          const rowCount = subrow.length;
          const rowWidth = rowCount * scaledCardWidth + gap * Math.max(0, rowCount - 1);
          const rowStartX = Math.max(horizontalPadding, (boardRect.width - rowWidth) / 2);
          const yOffset = runningYOffset;

          subrow.forEach(({ order }, columnIndex) => {
            const key = toKey(order.ticket_instance_id, order.card);
            targetLayouts.push({
              key,
              ticketInstanceId: order.ticket_instance_id,
              orderId: order.order_id,
              card: Number(order.card) || 0,
              scale: targetScale,
              x: Math.round(rowStartX + columnIndex * (scaledCardWidth + gap)),
              y: Math.round(verticalPadding + yOffset),
              zIndex: nextZIndex,
            });

            nextZIndex += 1;
          });

          const subrowHeight = rowHeights[groupIndex]?.[subrowIndex] || 0;
          runningYOffset += subrowHeight;
          const isLastSubrowInGroup = subrowIndex === subrows.length - 1;
          const isLastGroup = groupIndex === wrappedGroupEntries.length - 1;
          if (!isLastSubrowInGroup || !isLastGroup) {
            runningYOffset += gap;
          }
        });
      });
    } else if (mode === "compact-pack") {
      const compactGap = 8;
      const compactScale = FREEFORM_TICKET_MIN_SCALE;
      const compactCardWidth = FREEFORM_TICKET_BASE_WIDTH * compactScale;
      const columns = Math.max(
        1,
        Math.floor((Math.max(0, boardRect.width - horizontalPadding * 2) + compactGap) / (compactCardWidth + compactGap))
      );
      const rows = Math.max(1, Math.ceil(layoutCards.length / columns));
      const rowAssignments = layoutCards.map((_, index) => Math.floor(index / columns));
      const rowHeights = new Array(rows).fill(0);

      layoutCards.forEach(({ order }, index) => {
        const key = toKey(order.ticket_instance_id, order.card);
        const contentNode = contentRefs.current.get(key);
        const row = rowAssignments[index];
        const baseHeight = contentNode?.offsetHeight || 0;
        rowHeights[row] = Math.max(rowHeights[row], baseHeight * compactScale);
      });

      layoutCards.forEach(({ order }, index) => {
        const key = toKey(order.ticket_instance_id, order.card);
        const row = rowAssignments[index];
        const column = index % columns;
        const yOffset =
          row === 0
            ? 0
            : rowHeights.slice(0, row).reduce((sum, height) => sum + height, 0) + compactGap * row;

        targetLayouts.push({
          key,
          ticketInstanceId: order.ticket_instance_id,
          orderId: order.order_id,
          card: Number(order.card) || 0,
          scale: compactScale,
          x: Math.round(horizontalPadding + column * (compactCardWidth + compactGap)),
          y: Math.round(verticalPadding + yOffset),
          zIndex: index + 1,
        });
      });
    } else if (mode === "dense-grid") {
      const compactGap = 8;
      const compactScale = FREEFORM_TICKET_MIN_SCALE;
      const compactCardWidth = FREEFORM_TICKET_BASE_WIDTH * compactScale;
      const denseHorizontalPadding = 0;
      const denseVerticalPadding = 0;
      const columns = Math.max(
        1,
        Math.floor((Math.max(0, boardRect.width - denseHorizontalPadding * 2) + compactGap) / (compactCardWidth + compactGap))
      );
      const gridWidth =
        columns * compactCardWidth + compactGap * Math.max(0, columns - 1);
      const startX = Math.max(denseHorizontalPadding, (boardRect.width - gridWidth) / 2);
      const columnHeights = new Array(columns).fill(denseVerticalPadding);

      layoutCards.forEach(({ order }, index) => {
        const key = toKey(order.ticket_instance_id, order.card);
        const contentNode = contentRefs.current.get(key);
        const baseHeight = contentNode?.offsetHeight || 0;
        const scaledHeight = baseHeight * compactScale;
        const targetColumn = columnHeights.reduce((bestIndex, height, currentIndex) => {
          return height < columnHeights[bestIndex] ? currentIndex : bestIndex;
        }, 0);
        const x = startX + targetColumn * (compactCardWidth + compactGap);
        const y = columnHeights[targetColumn];

        targetLayouts.push({
          key,
          ticketInstanceId: order.ticket_instance_id,
          orderId: order.order_id,
          card: Number(order.card) || 0,
          scale: compactScale,
          x: Math.round(x),
          y: Math.round(y),
          zIndex: index + 1,
        });

        columnHeights[targetColumn] += scaledHeight + compactGap;
      });
    } else {
      const minScaledWidth = FREEFORM_TICKET_BASE_WIDTH * FREEFORM_TICKET_MIN_SCALE;
      const maxColumnsThatFit = Math.max(
        1,
        Math.floor((Math.max(0, boardRect.width - horizontalPadding * 2) + gap) / (minScaledWidth + gap))
      );
      const columns = Math.max(1, Math.min(layoutCards.length, maxColumnsThatFit));
      const rows = Math.max(1, Math.ceil(layoutCards.length / columns));
      const rowAssignments = layoutCards.map((_, index) => Math.floor(index / columns));
      const rowHeights = new Array(rows).fill(0);

      layoutCards.forEach(({ order }, index) => {
        const key = toKey(order.ticket_instance_id, order.card);
        const contentNode = contentRefs.current.get(key);
        const row = rowAssignments[index];
        const baseHeight = contentNode?.offsetHeight || 0;
        rowHeights[row] = Math.max(rowHeights[row], baseHeight);
      });

      const availableWidth = Math.max(
        0,
        boardRect.width - horizontalPadding * 2 - gap * Math.max(0, columns - 1)
      );
      const widthScale = availableWidth / Math.max(1, columns * FREEFORM_TICKET_BASE_WIDTH);
      const targetScale = clampScale(Math.min(widthScale, 1));
      const scaledCardWidth = FREEFORM_TICKET_BASE_WIDTH * targetScale;
      const rowScaledHeights = rowHeights.map((height) => height * targetScale);

      layoutCards.forEach(({ order }, index) => {
        const key = toKey(order.ticket_instance_id, order.card);
        const row = rowAssignments[index];
        const column = index % columns;
        const rowCount = Math.min(columns, Math.max(1, layoutCards.length - row * columns));
        const rowWidth =
          rowCount * scaledCardWidth + gap * Math.max(0, rowCount - 1);
        const rowStartX = Math.max(horizontalPadding, (boardRect.width - rowWidth) / 2);
        const yOffset =
          row === 0 ? 0 : rowScaledHeights.slice(0, row).reduce((sum, height) => sum + height, 0) + gap * row;

        targetLayouts.push({
          key,
          ticketInstanceId: order.ticket_instance_id,
          orderId: order.order_id,
          card: Number(order.card) || 0,
          scale: targetScale,
          x: Math.round(rowStartX + column * (scaledCardWidth + gap)),
          y: Math.round(verticalPadding + yOffset),
          zIndex: index + 1,
        });
      });
    }

    targetLayouts.forEach((targetLayout, index) => {
      const draggable = draggableRefs.current.get(targetLayout.key);
      const node = elementRefs.current.get(targetLayout.key);
      const updateScaledFrame = updateScaledFrameRefs.current.get(targetLayout.key);
      if (!draggable || !node || !updateScaledFrame) return;

      animationRefs.current.get(targetLayout.key)?.pause?.();

      const startState = {
        x: Number(draggable.x) || 0,
        y: Number(draggable.y) || 0,
        scale:
          Number(node.dataset.rawScale) ||
          getScaleForLayout(layouts[targetLayout.key]) ||
          1,
      };

      const nextAnimation = animate(startState, {
        x: targetLayout.x,
        y: targetLayout.y,
        scale: targetLayout.scale,
        duration: 550 + index * 35,
        ease: "outQuad",
        onBegin: () => {
          node.dataset.rawZIndex = String(targetLayout.zIndex);
          node.style.willChange = "transform";
          if (contentRefs.current.get(targetLayout.key)) {
            contentRefs.current.get(targetLayout.key).style.willChange = "transform";
          }
        },
        onUpdate: () => {
          updateScaledFrame(startState.scale, { resizeFrame: false });
          draggable.setX(startState.x, true);
          draggable.setY(startState.y, true);
        },
        onComplete: async () => {
          animationRefs.current.delete(targetLayout.key);
          updateScaledFrame(targetLayout.scale);
          node.style.willChange = "";
          if (contentRefs.current.get(targetLayout.key)) {
            contentRefs.current.get(targetLayout.key).style.willChange = "";
          }
          await onPersistLayout?.({
            key: targetLayout.key,
            stationId,
            ticketInstanceId: targetLayout.ticketInstanceId,
            orderId: targetLayout.orderId,
            card: targetLayout.card,
            x: targetLayout.x,
            y: targetLayout.y,
            zIndex: targetLayout.zIndex,
            scale: targetLayout.scale,
          });
        },
      });

      animationRefs.current.set(targetLayout.key, nextAnimation);
    });
  }, [layouts, onPersistLayout, sortOrderedCards, sortRequest, stationId]);

  return (
    <div
      ref={boardFrameRef}
      className={`relative w-full overflow-hidden rounded-2xl border border-red-300 bg-red-200/70 dark:border-red-900 dark:bg-red-950/40 ${
      isPeekMode || isFireCourseMode || isFireItemMode || isPrintCourseMode ? "z-50" : "z-20"
    }`}
      style={{ minHeight: boardHeight ? `${boardHeight}px` : undefined }}
    >
      <div ref={boardSurfaceRef} className="absolute inset-0 overflow-hidden">
        {orderedCards.map(({ id, order, orderIndex }) => {
          const layout = layouts[toKey(order.ticket_instance_id, order.card)] || {};
          const scale = getScaleForLayout(layout);
          return (
            <div
              key={id}
              ref={(node) => {
                if (node) {
                  elementRefs.current.set(toKey(order.ticket_instance_id, order.card), node);
                } else {
                  elementRefs.current.delete(toKey(order.ticket_instance_id, order.card));
                }
              }}
              className="absolute left-0 top-0 touch-none"
              style={{
                zIndex:
                  cssLayerByKey.get(toKey(order.ticket_instance_id, order.card)) ||
                  FREEFORM_TICKET_BASE_LAYER,
                width: getWidthForScale(scale),
                minWidth: getWidthForScale(FREEFORM_TICKET_MIN_SCALE),
                maxWidth: "calc(100% - 2rem)",
              }}
            >
              <div
                ref={(node) => {
                  if (node) {
                    contentRefs.current.set(toKey(order.ticket_instance_id, order.card), node);
                  } else {
                    contentRefs.current.delete(toKey(order.ticket_instance_id, order.card));
                  }
                }}
                className="w-[420px] origin-top-left"
                style={{ transform: `scale(${scale})` }}
              >
                <TicketCard
                  order={order}
                  orderIndex={orderIndex}
                  totalCards={1}
                  onItemTap={(ticketInstanceId, itemId) => onItemTap(ticketInstanceId, itemId)}
                  onItemDoubleTap={(ticketInstanceId, itemId) => onItemDoubleTap(ticketInstanceId, itemId)}
                  onPrepItemReady={onPrepItemReady}
                  onBump={onBump}
                  settings={settings}
                  isPeekMode={isPeekMode}
                  isFireCourseMode={isFireCourseMode}
                  onSelectFireCourse={onSelectFireCourse}
                  setIsFireCourseMode={setIsFireCourseMode}
                  isFireItemMode={isFireItemMode}
                  onSelectFireItem={onSelectFireItem}
                  isPrintCourseMode={isPrintCourseMode}
                  onSelectPrintCourse={onSelectPrintCourse}
                  isPrintTicketMode={isPrintTicketMode}
                  setPeekedOrder={setPeekedOrder}
                  setPeekModeIntent={setPeekModeIntent}
                  onBumpCourse={onBumpCourse}
                  onCourseComplete={onCourseComplete}
                  onPeekSelect={onPeekSelect}
                  disableBottomCutoffFade
                  disableModeBlur
                  dragHandleMode="header"
                dragHandleClassName="freeform-drag-handle"
                onDragHandleTouchStart={() => onDragStateChange?.(true)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
